<template>
  <div class="modern-alert" :class="`modern-alert--${variant}`">
    <div class="modern-alert__icon">
      <!-- Warning icon -->
      <svg v-if="variant === 'warning'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <!-- Error icon -->
      <svg v-else-if="variant === 'error'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
      </svg>
      <!-- Info icon -->
      <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
    </div>
    <div class="modern-alert__content">
      <slot></slot>
    </div>
    <button v-if="dismissible" class="modern-alert__close" @click="$emit('dismiss')">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  </div>
</template>

<script>
export default {
  name: 'ModernAlert',
  props: {
    variant: {
      type: String,
      default: 'info',
      validator: (value) => ['warning', 'error', 'info'].includes(value),
    },
    dismissible: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/globals/_variables.scss";

$alert-warning: $orange;
$alert-error: #e53935;
$alert-info: $primary;

.modern-alert {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  margin: 16px 0;
  border-radius: 16px;
  border-left: 4px solid;

  &--warning {
    border-left-color: $alert-warning;

    .modern-alert__icon {
      background: rgba($alert-warning, 0.12);
      color: $alert-warning;
    }

    :deep(strong) {
      color: $alert-warning;
    }
  }

  &--error {
    border-left-color: $alert-error;

    .modern-alert__icon {
      background: rgba($alert-error, 0.12);
      color: $alert-error;
    }

    :deep(strong) {
      color: $alert-error;
    }
  }

  &--info {
    border-left-color: $alert-info;

    .modern-alert__icon {
      background: rgba($alert-info, 0.12);
      color: $alert-info;
    }

    :deep(strong) {
      color: $alert-info;
    }
  }

  &__icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  &__content {
    flex: 1;
    font-size: 17px;
    line-height: 1.6;
    color: inherit;

    :deep(p) {
      margin-bottom: 0;
      color: inherit;
    }

    :deep(a) {
      color: $primary;
      text-decoration: underline;
      text-underline-offset: 2px;
      transition: color 0.2s ease;

      &:hover {
        color: lighten($primary, 10%);
      }
    }
  }

  &__close {
    all: unset;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    cursor: pointer;
    opacity: 0.5;
    transition: opacity 0.2s ease, background 0.2s ease;

    &:hover {
      opacity: 1;
      background: rgba(128, 128, 128, 0.1);
    }
  }
}

// Light theme
.theme--light .modern-alert {
  &--warning {
    background: #fef9f3;
  }

  &--error {
    background: #fef2f2;
  }

  &--info {
    background: #f0f7ff;
  }

  .modern-alert__content {
    color: $dark-font;
  }
}

// Dark theme
.theme--dark .modern-alert {
  &--warning {
    background: rgba($alert-warning, 0.08);
  }

  &--error {
    background: rgba($alert-error, 0.08);
  }

  &--info {
    background: rgba($alert-info, 0.08);
  }

  .modern-alert__content {
    color: rgba(255, 255, 255, 0.9);
  }
}
</style>
